import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../design-system/Modal';
import { Button } from '../design-system/Button';
import { TextArea } from '../common/TextArea';
import { useSubmitFeedback, type FeedbackType } from '../../api/hooks/feedback.hook';
import { Icon } from '../common/Icons';
import { cn } from '../../utils/cn';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEEDBACK_TYPES: Array<{
  value: FeedbackType;
  label: string;
  description: string;
  icon: string;
}> = [
  { value: 'general', label: 'General', description: 'General thoughts or comments', icon: 'Chat' },
  { value: 'thought', label: 'Thought', description: 'Share your thoughts', icon: 'Message' },
  {
    value: 'feature_wish',
    label: 'Feature Wish',
    description: 'Request a new feature',
    icon: 'Actions',
  },
  { value: 'problem', label: 'Problem', description: 'Report an issue or bug', icon: 'Warning' },
];

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general');
  const submitFeedback = useSubmitFeedback();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedbackText.trim()) {
      return;
    }

    try {
      await submitFeedback.mutateAsync({
        feedback_text: feedbackText.trim(),
        feedback_type: feedbackType,
      });

      setFeedbackText('');
      setFeedbackType('general');
      onClose();
    } catch (error) {
      // Error handling is done in the hook via toast
    }
  };

  const handleClose = () => {
    if (!submitFeedback.isPending) {
      setFeedbackText('');
      setFeedbackType('general');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
        <ModalHeader className="pb-4 sm:pb-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-xl sm:rounded-2xl bg-primary-600">
              <Icon name="Feedback" size={20} color="#000000" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-neutral-900 mb-0.5 sm:mb-1">
                Share Your Feedback
              </h2>
              <p className="text-xs sm:text-sm text-neutral-500 leading-relaxed">
                Help us improve by sharing your thoughts. We will implement your feedback in less
                than 24 hours.
              </p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="py-4 sm:py-6">
          <div className="space-y-5 sm:space-y-6">
            <div>
              <label className="mb-2.5 sm:mb-3 block text-sm font-semibold text-neutral-900">
                What type of feedback is this?
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                {FEEDBACK_TYPES.map((type) => {
                  const isSelected = feedbackType === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedbackType(type.value)}
                      className={cn(
                        'group relative rounded-xl sm:rounded-2xl border p-3 sm:p-4 text-left transition-all duration-200',
                        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 sm:focus:ring-offset-2',
                        'active:scale-[0.98] sm:hover:scale-[1.01]',
                        isSelected
                          ? 'border-primary-500 bg-primary-50 shadow-sm ring-1 sm:ring-2 ring-primary-500/20'
                          : 'border-neutral-200 bg-white sm:hover:border-primary-300 sm:hover:bg-neutral-50',
                      )}
                      aria-pressed={isSelected}
                    >
                      <div className="flex items-center gap-2.5 sm:gap-3">
                        <div
                          className={cn(
                            'flex h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-lg sm:rounded-xl transition-all',
                            isSelected
                              ? 'bg-primary-600 shadow-sm sm:shadow-md'
                              : 'bg-neutral-100 sm:group-hover:bg-primary-100',
                          )}
                        >
                          <Icon
                            name={type.icon as 'Chat' | 'Message' | 'Actions' | 'Warning'}
                            size={18}
                            color={isSelected ? '#000000' : '#6b7280'}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div
                            className={cn(
                              'text-sm font-semibold mb-0.5 transition-colors',
                              isSelected ? 'text-primary-900' : 'text-neutral-900',
                            )}
                          >
                            {type.label}
                          </div>
                          <div className="text-xs text-neutral-500 leading-relaxed truncate">
                            {type.description}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex-shrink-0">
                            <div className="h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-primary-600 flex items-center justify-center shadow-sm">
                              <svg
                                className="h-3 w-3 sm:h-4 sm:w-4 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2.5}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <TextArea
                value={feedbackText}
                onChange={setFeedbackText}
                placeholder="Tell us what's on your mind..."
                rows={5}
                label="Your Feedback"
                helperText="Please share any thoughts, feature wishes, or problems you've experienced."
                disabled={submitFeedback.isPending}
                required
                className="min-h-[100px] sm:min-h-[120px]"
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="pt-4 sm:pt-5 pb-4 sm:pb-5 flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={submitFeedback.isPending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={submitFeedback.isPending}
            disabled={!feedbackText.trim() || submitFeedback.isPending}
            className="w-full sm:w-auto"
          >
            Submit Feedback
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
